import { DateTime } from "luxon";
import { DatagridConfigurable, FunctionField, List, SelectInput, TextField, useRecordContext } from "react-admin";
import { grants } from "../../backoffice.access_control";
import { ServiceTimeFilter } from "../../inputs/ServiceTimeFilter";
import { AccessDenied } from "../../misc/AccessDenied";
import { EmptyState } from "../../misc/EmptyState";
import { PageTitle } from "../../misc/PageTitle";
import { DateField } from "../../fields/DateField";
import { MoneyField } from "../../fields/MoneyField";
import { useAutovioContext } from "../../hooks/useAutovioContext";
import type { DrivingSchool } from "../../providers/drivingSchoolsProvider";
import type { ManualPaymentRecord, ManualPaymentType } from "../../providers/manualPaymentsProvider";

const paymentTypeChoices: Array<{ id: ManualPaymentType; name: string }> = [
  { id: "cash", name: "Barzahlung" },
  { id: "bank_transfer", name: "Banküberweisung" },
];

const paymentTypeLabels: Record<ManualPaymentType, string> = {
  cash: "Barzahlung",
  bank_transfer: "Banküberweisung",
  external_bank_transfer: "Externe Banküberweisung",
};

export function DrivingSchoolManualPayments() {
  const [{ drivingSchoolId }] = useAutovioContext();
  const drivingSchool = useRecordContext<DrivingSchool>();

  if (!grants.has("viewPerformanceOverview")) {
    return (
      <>
        <PageTitle>Manuelle Einzahlungen</PageTitle>
        <AccessDenied />
      </>
    );
  }

  if (!drivingSchool || drivingSchool.id !== drivingSchoolId) {
    return null;
  }

  const availablePaymentTypeChoices = drivingSchool.customizations.includes("canUseExternalBankTransfer")
    ? [...paymentTypeChoices, { id: "external_bank_transfer" as const, name: "Externe Banküberweisung" }]
    : paymentTypeChoices;

  return (
    <>
      <PageTitle>Manuelle Einzahlungen</PageTitle>
      <List
        resource="manualPayments"
        title=" "
        storeKey={`DrivingSchoolManualPayments@${drivingSchoolId}`}
        filter={{ drivingSchoolId }}
        filters={[
          <ServiceTimeFilter key="serviceTime" alwaysOn />,
          <SelectInput
            key="paymentType"
            source="paymentType"
            label="Zahlungsart"
            choices={availablePaymentTypeChoices}
            alwaysOn
          />,
        ]}
        filterDefaultValues={{ serviceTime: DateTime.now().toFormat("yyyy-MM") }}
        sort={{ field: "created_at", order: "DESC" }}
        empty={<EmptyState noEntriesText="Noch keine manuelle Einzahlung vorhanden." />}
      >
        <DatagridConfigurable
          bulkActionButtons={false}
          empty={
            <EmptyState
              noEntriesText="Noch keine manuelle Einzahlung vorhanden."
              noMatchingEntriesText="Keine passenden manuellen Einzahlungen gefunden."
            />
          }
        >
          <DateField source="created_at" label="Erfasst am" showTime />
          <FunctionField<ManualPaymentRecord>
            source="payment_type"
            label="Zahlungsart"
            render={(record) => paymentTypeLabels[record.payment_type]}
          />
          {/* <ReferenceField source="created_by_id" reference="users" label="Erfasst von" link={false} emptyText="–">
            <TextField source="name" />
          </ReferenceField> */}
          <TextField source="student_name" label="Fahrschüler" />
          <MoneyField source="amount" label="Betrag" />
        </DatagridConfigurable>
      </List>
    </>
  );
}
